// Group detail — App Flow §5: roster, invite, occasions, pets, admin overrides.
import {
  Avatar,
  Button,
  Card,
  CaretLeft,
  ConfirmDialog,
  DatePicker,
  EmptyState,
  Input,
  LoadingState,
  MemberRow,
  PageHeader,
  Section,
  Select,
  Sheet,
  StatusPill,
  useToast,
} from '@roots/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { daysUntil, occasionLine } from '../lib/format.js';
import { platform } from '../platform/web.js';

type Tab = 'roster' | 'occasions' | 'pets' | 'admin';

/** §3.12 — every destructive action goes through a confirmation dialog. */
type PendingConfirm =
  | { kind: 'remove-member'; id: string; name: string }
  | { kind: 'remove-pet'; id: string; name: string }
  | { kind: 'force-close' };

export function FamilyDetailScreen() {
  const { familyId } = useParams({ strict: false }) as { familyId: string };
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [tab, setTab] = useState<Tab>('roster');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [addOccasion, setAddOccasion] = useState(false);
  const [addPet, setAddPet] = useState(false);
  const [grantFor, setGrantFor] = useState<string | null>(null);
  const [grantDate, setGrantDate] = useState<string | null>('2024-01-01');
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['family', familyId],
    queryFn: () => api.family(familyId),
  });
  const { data: occasions } = useQuery({
    queryKey: ['occasions', familyId],
    queryFn: () => api.occasions(familyId),
  });

  const isAdmin = data?.my_membership.role === 'admin';
  const { data: invites } = useQuery({
    queryKey: ['invites', familyId],
    queryFn: () => api.listInvites(familyId),
    enabled: isAdmin,
  });
  const editorId = data?.current_cycle?.editor_user_id;
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['family', familyId] });
    void queryClient.invalidateQueries({ queryKey: ['occasions', familyId] });
  };

  const invite = useMutation({
    mutationFn: () => api.createInvite(familyId),
    onSuccess: async (result) => {
      setInviteLink(result.invite_link);
      const outcome = await platform.share({
        title: `Join ${data?.name ?? 'our family'} on Roots`,
        text: 'Come share a memory with us this month.',
        url: result.invite_link,
      });
      push(outcome === 'shared' ? 'Link ready to share!' : 'Link copied.');
    },
    onError: () => push('Something went wrong creating the link. Try again in a moment.', 'error'),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['invites', familyId] }),
  });

  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) => api.revokeInvite(familyId, inviteId),
    onSuccess: () => {
      push('Invite link revoked.');
      void queryClient.invalidateQueries({ queryKey: ['invites', familyId] });
    },
    onError: () => push('Something went wrong revoking the link. Try again in a moment.', 'error'),
  });

  const setRole = useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: 'admin' | 'member' }) =>
      api.setRole(familyId, uid, role),
    onSuccess: (_r, vars) => {
      push(vars.role === 'admin' ? 'They’re now an admin.' : 'They’re no longer an admin.');
      invalidate();
    },
    onError: () => push('Every family needs at least one admin.', 'error'),
  });

  const removeMember = useMutation({
    mutationFn: (uid: string) => api.removeMember(familyId, uid),
    onSuccess: () => {
      setConfirm(null);
      push('Member removed.');
      invalidate();
    },
    onError: () => push('Every family needs at least one admin.', 'error'),
  });

  const grantArchive = useMutation({
    mutationFn: ({ uid, from }: { uid: string; from: string }) =>
      api.grantArchive(familyId, uid, from),
    onSuccess: (_r, vars) => {
      push(`They can now see editions from ${vars.from} onward.`);
      setGrantFor(null);
      invalidate();
    },
  });

  const forceClose = useMutation({
    mutationFn: () => api.forceClose(familyId),
    onSuccess: () => {
      setConfirm(null);
      push('Cycle closed, compilation started.');
      invalidate();
    },
  });

  const removePet = useMutation({
    mutationFn: (petId: string) => api.deletePet(familyId, petId),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });

  const emergencyEditor = useMutation({
    mutationFn: (uid: string) => api.emergencyEditor(familyId, uid),
    onSuccess: () => {
      push('Editor updated.');
      invalidate();
    },
  });

  if (isLoading || !data) return <LoadingState onRetry={() => void refetch()} />;

  const tabs: [Tab, string][] = [
    ['roster', 'Roster'],
    ['occasions', 'Occasions'],
    ['pets', 'Pets'],
    ...(isAdmin ? ([['admin', 'Admin']] as [Tab, string][]) : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <Button
        variant="ghost"
        className="self-start md:hidden"
        onClick={() => void navigate({ to: '/families' })}
      >
        <CaretLeft size={18} aria-hidden /> Families
      </Button>
      <PageHeader
        title={data.name}
        subtitle={`${data.members.length} members · ${data.timezone}`}
        action={
          isAdmin ? (
            <Button onClick={() => invite.mutate()} loading={invite.isPending}>
              Invite
            </Button>
          ) : undefined
        }
      />

      {inviteLink && (
        <Card className="flex flex-col gap-2">
          <p className="type-caption">Share this link — it works for 7 days.</p>
          <code className="type-caption break-all">{inviteLink}</code>
        </Card>
      )}

      {isAdmin && (invites?.invites?.length ?? 0) > 0 && invites && (
        <Card className="flex flex-col gap-3">
          <p className="type-caption">Active invite links</p>
          {invites.invites
            .filter((i) => !i.revoked_at)
            .map((i) => {
              const left = daysUntil(i.expires_at);
              return (
                <div key={i.id} className="flex items-center justify-between gap-3">
                  <div className="flex flex-col gap-1">
                    <StatusPill tone={left !== null && left <= 1 ? 'warning' : 'neutral'}>
                      {left !== null && left > 0
                        ? `Expires in ${left} day${left === 1 ? '' : 's'}`
                        : 'Expires today'}
                    </StatusPill>
                    {i.use_count > 0 && (
                      <span className="type-caption">
                        Used {i.use_count} time{i.use_count === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => revokeInvite.mutate(i.id)}
                    loading={revokeInvite.isPending}
                  >
                    Revoke
                  </Button>
                </div>
              );
            })}
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        {tabs.map(([key, label]) => (
          <Button
            key={key}
            variant={tab === key ? 'primary' : 'ghost'}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === 'roster' && (
        <Section>
          <ul className="flex flex-col gap-2">
            {data.members.map((member) => (
              <MemberRow
                key={member.user_id}
                name={member.name}
                avatarUrl={member.avatar_url}
                membershipRole={member.role}
                status={member.status}
                isEditor={member.user_id === editorId}
                actions={
                  isAdmin && member.user_id !== user?.id ? (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          setRole.mutate({
                            uid: member.user_id,
                            role: member.role === 'admin' ? 'member' : 'admin',
                          })
                        }
                      >
                        {member.role === 'admin' ? 'Revoke admin' : 'Make admin'}
                      </Button>
                      <Button variant="ghost" onClick={() => setGrantFor(member.user_id)}>
                        Archive
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() =>
                          setConfirm({
                            kind: 'remove-member',
                            id: member.user_id,
                            name: member.name,
                          })
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  ) : undefined
                }
              />
            ))}
          </ul>
          {data.members.length === 1 && (
            <EmptyState
              message="It’s just you for now. Invite your family to get started!"
              actions={
                isAdmin ? (
                  <Button onClick={() => invite.mutate()}>Invite members</Button>
                ) : undefined
              }
            />
          )}
        </Section>
      )}

      {tab === 'occasions' && (
        <Section
          title="Occasion calendar"
          action={<Button onClick={() => setAddOccasion(true)}>Add occasion</Button>}
        >
          {(occasions?.occasions.length ?? 0) === 0 ? (
            <EmptyState
              message="No occasions yet. Add birthdays so Roots can celebrate them!"
              actions={<Button onClick={() => setAddOccasion(true)}>Add occasion</Button>}
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {occasions?.occasions.map((o) => (
                <li
                  key={o.id}
                  className="flex items-center gap-3 rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] p-3"
                >
                  <Avatar name={o.subject_name} src={o.subject_avatar} size={32} />
                  <span className="type-body flex-1">{occasionLine(o)}</span>
                  <span className="type-caption">
                    {o.recurrence === 'annual' ? 'Yearly' : 'Once'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {tab === 'pets' && (
        <Section title="Pets" action={<Button onClick={() => setAddPet(true)}>Add a pet</Button>}>
          {data.pets.length === 0 ? (
            <EmptyState message="No pets in this family yet. Got a furry (or scaly) family member?" />
          ) : (
            <ul className="flex flex-col gap-2">
              {data.pets.map((pet) => (
                <li
                  key={pet.id}
                  className="flex items-center gap-3 rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] p-3"
                >
                  <Avatar name={pet.name} src={pet.avatar_url} size={32} />
                  <span className="type-body flex-1">
                    {pet.name} · {pet.species}
                  </span>
                  {(isAdmin || pet.created_by === user?.id) && (
                    <Button
                      variant="destructive"
                      onClick={() => setConfirm({ kind: 'remove-pet', id: pet.id, name: pet.name })}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {tab === 'admin' && isAdmin && (
        <Section title="Admin overrides">
          <Card className="flex flex-col gap-3">
            <p className="type-body">Force this month’s window closed and start compiling.</p>
            <Button
              variant="destructive"
              onClick={() => setConfirm({ kind: 'force-close' })}
              disabled={data.current_cycle?.status !== 'open'}
            >
              Force cycle close
            </Button>
          </Card>
          <Card className="flex flex-col gap-3">
            <p className="type-body">Hand this month’s editor role to someone else.</p>
            <ul className="flex flex-col gap-2">
              {data.members
                .filter((m) => m.user_id !== editorId && m.status === 'active')
                .map((m) => (
                  <li key={m.user_id} className="flex items-center justify-between gap-2">
                    <span className="type-body">{m.name}</span>
                    <Button variant="secondary" onClick={() => emergencyEditor.mutate(m.user_id)}>
                      Make editor
                    </Button>
                  </li>
                ))}
            </ul>
          </Card>
        </Section>
      )}

      <AddOccasionSheet
        open={addOccasion}
        onOpenChange={setAddOccasion}
        familyId={familyId}
        members={data.members}
        pets={data.pets}
        isAdmin={Boolean(isAdmin)}
        myUserId={user?.id ?? ''}
        onDone={invalidate}
      />

      <AddPetSheet open={addPet} onOpenChange={setAddPet} familyId={familyId} onDone={invalidate} />

      <ConfirmDialog
        open={Boolean(grantFor)}
        onOpenChange={(open) => !open && setGrantFor(null)}
        title="Unlock earlier editions"
        description="Pick the date they should be able to read from."
        confirmLabel="Grant access"
        loading={grantArchive.isPending}
        disabled={!grantDate}
        onConfirm={() =>
          grantFor && grantDate && grantArchive.mutate({ uid: grantFor, from: grantDate })
        }
      >
        <DatePicker label="From" value={grantDate} onChange={setGrantDate} />
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        destructive
        title={
          confirm?.kind === 'force-close'
            ? 'Close this month early?'
            : `Remove ${confirm && 'name' in confirm ? confirm.name : ''}?`
        }
        description={
          confirm?.kind === 'force-close'
            ? 'Prompts close now and the edition starts compiling. This can’t be undone.'
            : confirm?.kind === 'remove-member'
              ? 'They’ll lose access to this family. Their past contributions stay in published editions.'
              : 'They’ll be removed from this family’s roster and future editions.'
        }
        confirmLabel={confirm?.kind === 'force-close' ? 'Close now' : 'Remove'}
        loading={removeMember.isPending || removePet.isPending || forceClose.isPending}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.kind === 'remove-member') removeMember.mutate(confirm.id);
          else if (confirm.kind === 'remove-pet') removePet.mutate(confirm.id);
          else forceClose.mutate();
        }}
      />
    </div>
  );
}

function AddOccasionSheet({
  open,
  onOpenChange,
  familyId,
  members,
  pets,
  isAdmin,
  myUserId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyId: string;
  members: { user_id: string; name: string }[];
  pets: { id: string; name: string }[];
  isAdmin: boolean;
  myUserId: string;
  onDone: () => void;
}) {
  const { push } = useToast();
  const [subject, setSubject] = useState(`member:${myUserId}`);
  const [type, setType] = useState<'birthday' | 'anniversary' | 'custom'>('birthday');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState<string | null>(null);
  const [recurrence, setRecurrence] = useState<'annual' | 'one_time'>('annual');

  const create = useMutation({
    mutationFn: () => {
      const [subjectType, subjectId] = subject.split(':') as ['member' | 'pet', string];
      return api.createOccasion(familyId, {
        subject_type: subjectType,
        subject_id: subjectId,
        occasion_type: type,
        title: title || `${type}`,
        date,
        recurrence,
      });
    },
    onSuccess: () => {
      push('Added to the calendar!');
      onOpenChange(false);
      onDone();
    },
    onError: () => push('Only admins can add occasions for other people.', 'error'),
  });

  const options = isAdmin
    ? [
        ...members.map((m) => [`member:${m.user_id}`, m.name] as const),
        ...pets.map((p) => [`pet:${p.id}`, `${p.name} 🐾`] as const),
      ]
    : [[`member:${myUserId}`, 'Me'] as const];

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Add an occasion">
      <div className="flex flex-col gap-3">
        <Select
          id="occasion-subject"
          label="Who’s it for"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        >
          {options.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select
          id="occasion-type"
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value as typeof type)}
        >
          <option value="birthday">Birthday</option>
          <option value="anniversary">Anniversary</option>
          <option value="custom">Something else</option>
        </Select>

        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <DatePicker label="Date" value={date} onChange={setDate} />
        <Select
          id="occasion-recurrence"
          label="Repeats"
          value={recurrence}
          onChange={(e) => setRecurrence(e.target.value as typeof recurrence)}
        >
          <option value="annual">Every year</option>
          <option value="one_time">Just once</option>
        </Select>

        <Button onClick={() => create.mutate()} disabled={!date} loading={create.isPending}>
          Add occasion
        </Button>
      </div>
    </Sheet>
  );
}

function AddPetSheet({
  open,
  onOpenChange,
  familyId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyId: string;
  onDone: () => void;
}) {
  const { push } = useToast();
  const [name, setName] = useState('');
  const [species, setSpecies] = useState('');
  const [birthday, setBirthday] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createPet(familyId, { name, species, birthday }),
    onSuccess: () => {
      push(`${name} is officially part of the family! 🐾`);
      onOpenChange(false);
      setName('');
      setSpecies('');
      onDone();
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Add a pet">
      <div className="flex flex-col gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input
          label="Species"
          value={species}
          onChange={(e) => setSpecies(e.target.value)}
          placeholder="dog, cat, hamster…"
        />
        <DatePicker label="Birthday (optional)" value={birthday} onChange={setBirthday} />
        <Button
          onClick={() => create.mutate()}
          disabled={!name || !species}
          loading={create.isPending}
        >
          Add pet
        </Button>
      </div>
    </Sheet>
  );
}
